import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Board from "../components/Board";
import PlayerHUD from "../components/PlayerHUD";
import CardView from "../components/Card";
import HowToPlay from "../components/HowToPlay";
import ResultModal from "../components/ResultModal";
import {
  PLAYERS, createInitialState, drawCard, placeNormal, applySpecial,
  legalNormalMoves, legalSpecialTargets, isGameOver, skipTurn, SPECIAL_LABEL, SPECIAL_DESC,
  rerollDrawnCard,
} from "../lib/quattro";
import { chooseAIMove, applyAIMove } from "../lib/ai";
import { spendGems, GEM_PRICES } from "../lib/gems";
import { fetchOrCreateProfile, recordGame, getStoredName } from "../lib/api";
import { sfx, isMuted, setMuted } from "../lib/sound";
import { hapticLight, hapticMedium, hapticSuccess, hapticError, vibrate } from "../lib/native";
import { recordSoloGameFinished, maybeShowInterstitial, markRewardedAdShown } from "../lib/interstitial";

const HUMAN_ID = 0;
const BET = 100;

function getDifficulty() {
  try { return localStorage.getItem("quattro_difficulty") || "medium"; } catch { return "medium"; }
}

// Build a fake state with a winning line for visual demo (?demoWin=1).
function buildDemoWinState() {
  const s = createInitialState(1);
  // Place a yellow 4-in-a-row across the top row
  for (let c = 0; c < 4; c++) {
    s.board[0][c].stack.push({ id: `demo-${c}`, owner: 0, power: 5, special: null });
  }
  s.winner = 0;
  s.winLine = [[0, 0], [0, 1], [0, 2], [0, 3]];
  return s;
}

export default function Game() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(() =>
    searchParams.get("demoWin") === "1" ? buildDemoWinState() : createInitialState()
  );
  const [profile, setProfile] = useState(null);
  const [howto, setHowto] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [chipsDelta, setChipsDelta] = useState(0);
  const [recorded, setRecorded] = useState(false);
  const recordingRef = useRef(false);
  const lastTurnRef = useRef(state.currentPlayer);
  const lastDrawIdRef = useRef(null);
  const prevStateRef = useRef(state);
  const [difficulty, setDifficultyState] = useState(getDifficulty());
  const [mutedState, setMutedState] = useState(isMuted());

  // Power-up state — single-player only. Caps reset per game (in restart()).
  const REROLL_CAP_PER_GAME = 1;
  const [rerollsLeft, setRerollsLeft] = useState(REROLL_CAP_PER_GAME);
  const [powerupBusy, setPowerupBusy] = useState(false);
  const [powerupErr, setPowerupErr] = useState("");

  const toggleMute = () => {
    const next = !mutedState;
    setMuted(next);
    setMutedState(next);
    if (!next) sfx.click();
  };

  const playerNames = useMemo(() => {
    const myName = profile?.name || getStoredName() || "You";
    return [myName, "Bot Cyan", "Bot Crimson", "Bot Violet"];
  }, [profile]);

  // Load profile
  useEffect(() => {
    (async () => {
      try {
        const p = await fetchOrCreateProfile(getStoredName());
        setProfile(p);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Auto-draw at start of each turn
  useEffect(() => {
    if (state.winner !== null) return;
    if (state.drawnCard) return;
    if (isGameOver(state)) return;
    const t = setTimeout(() => setState((s) => drawCard(s)), 400);
    return () => clearTimeout(t);
  }, [state]);

  // Bot turn — delay matches the online BOT_THINK_MS (rooms.py) so single-player
  // and multiplayer share the same humanized pace. Bots no longer feel like
  // instant-snap reflex agents.
  useEffect(() => {
    if (state.winner !== null) return;
    if (state.currentPlayer === HUMAN_ID) return;
    if (!state.drawnCard) return;
    const t = setTimeout(() => {
      setState((s) => {
        if (s.currentPlayer === HUMAN_ID || !s.drawnCard) return s;
        const move = chooseAIMove(s, difficulty);
        if (!move) return skipTurn(s);
        return applyAIMove(s, move);
      });
    }, 2200);
    return () => clearTimeout(t);
  }, [state.currentPlayer, state.drawnCard, state.winner, state.turnCount, difficulty]);

  // End game effects
  useEffect(() => {
    const over = state.winner !== null ||
      (state.decks.every(d => d.length === 0) && !state.drawnCard);
    if (!over || resultOpen || recordingRef.current) return;
    if (recorded) return;
    recordingRef.current = true;
    const won = state.winner === HUMAN_ID;
    const delta = state.winner == null ? 0 : (won ? BET * 3 : -BET);
    (async () => {
      try {
        if (state.winner != null) {
          const p = await recordGame(won, BET);
          setProfile(p);
          try {
            const { track } = await import("../lib/analytics");
            track("game_ended", { mode: "vs_bots", won, bet: BET, winner: state.winner });
          } catch {}
        }
      } catch (e) { console.error(e); }
      setChipsDelta(delta);
      setRecorded(true);
      // Bump the global solo-games counter so the interstitial gating policy
      // can decide whether the next "Lobby" / "Play Again" should fire an ad.
      recordSoloGameFinished();
      // Native in-app review prompt — only fires on Android, only after the
      // user has won several times (peak emotional moment per Google's
      // recommendation). No-op on web.
      if (won) {
        import("../lib/review").then(({ recordWinAndMaybePrompt }) => {
          recordWinAndMaybePrompt();
        }).catch(() => {});
      }
      setTimeout(() => setResultOpen(true), 2600); // wait for win-line beam + particle burst
    })();
  }, [state.winner, state.decks, state.drawnCard, recorded, resultOpen]);

  // Sound effects: detect transitions in state
  useEffect(() => {
    const prev = prevStateRef.current;
    // Card placed: turnCount incremented
    if (state.turnCount > prev.turnCount) {
      // Find the new placement: count board cards/blocks now vs before
      let placed = false, stacked = false, special = false, blocked = false;
      outer: for (let r = 0; r < 6; r++) for (let c = 0; c < 6; c++) {
        const cur = state.board[r][c], pr = prev.board[r][c];
        if (cur.blocked && !pr.blocked) { blocked = true; special = true; break outer; }
        if (cur.stack.length > pr.stack.length) {
          // either placed on empty (new card) or on top (stack grew)
          if (pr.stack.length === 0) placed = true; else stacked = true;
          break outer;
        }
        // power_up / power_down / color_switcher: stack lengths same but top changed
        if (cur.stack.length === pr.stack.length && cur.stack.length > 0) {
          const a = cur.stack[cur.stack.length - 1];
          const b = pr.stack[pr.stack.length - 1];
          if (a && b && (a.power !== b.power || a.owner !== b.owner)) { special = true; break outer; }
        }
      }
      if (special) { sfx.special(); hapticMedium(); vibrate(20); }
      else if (stacked) { sfx.stack(); hapticMedium(); vibrate(15); }
      else if (placed) { sfx.place(); hapticLight(); vibrate(8); }
      else if (blocked) { sfx.special(); hapticMedium(); vibrate(20); }
    }
    // Card drawn (new drawnCard id appears)
    const drawnId = state.drawnCard?.id;
    if (drawnId && drawnId !== lastDrawIdRef.current) {
      lastDrawIdRef.current = drawnId;
      sfx.draw();
    }
    // Turn became human's turn
    if (lastTurnRef.current !== state.currentPlayer) {
      lastTurnRef.current = state.currentPlayer;
      if (state.currentPlayer === HUMAN_ID && state.winner === null) sfx.turnSelf();
    }
    // Win / Lose
    if (state.winner !== null && prev.winner === null) {
      if (state.winner === HUMAN_ID) {
        sfx.win(); hapticSuccess(); vibrate([0, 60, 40, 60, 40, 120]);
      } else {
        sfx.lose(); hapticError(); vibrate([0, 80, 60, 80]);
      }
    }
    prevStateRef.current = state;
  }, [state]);

  const legalSet = useMemo(() => {
    if (state.winner !== null) return new Set();
    if (state.currentPlayer !== HUMAN_ID) return new Set();
    if (!state.drawnCard) return new Set();
    const moves = state.drawnCard.special
      ? legalSpecialTargets(state, state.drawnCard)
      : legalNormalMoves(state, state.drawnCard);
    return new Set(moves.map(([r, c]) => `${r},${c}`));
  }, [state]);

  // Auto-skip if no legal move
  useEffect(() => {
    if (state.currentPlayer !== HUMAN_ID) return;
    if (!state.drawnCard) return;
    if (state.winner !== null) return;
    const moves = state.drawnCard.special
      ? legalSpecialTargets(state, state.drawnCard)
      : legalNormalMoves(state, state.drawnCard);
    if (moves.length === 0) {
      const t = setTimeout(() => setState((s) => skipTurn(s)), 1000);
      return () => clearTimeout(t);
    }
  }, [state]);

  const onCellClick = useCallback((r, c) => {
    if (state.currentPlayer !== HUMAN_ID || !state.drawnCard) return;
    setState((s) => {
      if (!s.drawnCard) return s;
      if (s.drawnCard.special) return applySpecial(s, r, c);
      return placeNormal(s, r, c);
    });
  }, [state.currentPlayer, state.drawnCard]);

  const restart = () => {
    setState(createInitialState());
    setResultOpen(false);
    setRecorded(false);
    recordingRef.current = false;
    setChipsDelta(0);
    setRerollsLeft(REROLL_CAP_PER_GAME);
    setPowerupErr("");
  };

  const doReroll = async () => {
    if (powerupBusy || !isHumanTurn || rerollsLeft <= 0) return;
    setPowerupBusy(true); setPowerupErr("");
    try {
      const res = await spendGems("reroll");
      if (!res) {
        setPowerupErr("Not enough gems");
        setTimeout(() => setPowerupErr(""), 1800);
        return;
      }
      setProfile((p) => p ? { ...p, gems: res.gems } : p);
      setRerollsLeft((n) => n - 1);
      setState((s) => rerollDrawnCard(s));
      sfx.click();
    } catch (e) {
      setPowerupErr("Failed — try again");
      setTimeout(() => setPowerupErr(""), 1800);
    } finally {
      setPowerupBusy(false);
    }
  };

  const players = PLAYERS.map((p, i) => ({ ...p, label: playerNames[i] }));

  const drawn = state.drawnCard;
  const isHumanTurn = state.currentPlayer === HUMAN_ID && !!drawn && state.winner === null;
  const noLegal = isHumanTurn && legalSet.size === 0;

  return (
    <div className="min-h-screen w-full px-3 md:px-6 py-4 md:py-6 flex flex-col items-center">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">
        {/* Top header */}
        <div className="lg:col-span-12 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 md:gap-3">
            <button className="btn-ghost" onClick={() => nav("/")} data-testid="exit-btn">Exit</button>
            <div className="font-display font-black text-lg md:text-2xl tracking-tight">QUATTRO</div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
            <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-2" style={{ minHeight: 32 }}>
              <span className="label-eyebrow" style={{ fontSize: 10 }}>POT</span>
              <span className="font-mono font-bold" style={{ color: "#FFD700", fontSize: 12 }} data-testid="pot-amount">◆ {BET * 4}</span>
            </div>
            <div className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 hidden md:flex items-center gap-2" data-testid="game-difficulty-pill" style={{ minHeight: 32 }}>
              <span className="label-eyebrow">AI</span>
              <span className="font-mono font-bold uppercase" style={{ fontSize: 11 }}>{difficulty}</span>
            </div>
            <button
              className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 hover:bg-white/10"
              onClick={toggleMute}
              data-testid="game-mute-toggle"
              title={mutedState ? "Unmute" : "Mute"}
              style={{ border: "1px solid rgba(255,255,255,0.12)", minHeight: 32 }}
            >
              <span style={{ fontSize: 13 }}>{mutedState ? "🔇" : "🔊"}</span>
            </button>
            <button className="btn-ghost" onClick={() => setHowto(true)} data-testid="game-howto-btn">Rules</button>
          </div>
        </div>

        {/* Player HUD */}
        <div className="lg:col-span-12">
          <PlayerHUD
            players={players}
            decks={state.decks}
            currentPlayer={state.currentPlayer}
            drawnCard={state.drawnCard}
            humanId={HUMAN_ID}
            activeSkins={{ [HUMAN_ID]: profile?.active_skin || "default" }}
          />
        </div>

        {/* MOBILE-ONLY compact game bar (above board): turn + drawn card + power-ups */}
        <div className="lg:hidden glass rounded-2xl p-3" data-testid="mobile-game-bar">
          <div className="flex items-center gap-3">
            <div
              className="rounded-lg flex-shrink-0"
              style={{ width: 56, height: 76 }}
              data-testid="mobile-drawn-card"
            >
              {drawn ? <CardView card={drawn} glow /> : (
                <div className="w-full h-full rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 text-[9px] label-eyebrow">…</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="rounded-full flex-shrink-0" style={{
                  width: 8, height: 8, background: PLAYERS[state.currentPlayer].color,
                  boxShadow: `0 0 8px ${PLAYERS[state.currentPlayer].glow}`,
                }} />
                <div className="font-display font-bold text-sm truncate" data-testid="mobile-current-turn">
                  {playerNames[state.currentPlayer]}
                  {state.currentPlayer === HUMAN_ID && state.winner === null && (
                    <span className="text-white/55 text-xs ml-1">· your turn</span>
                  )}
                </div>
              </div>
              {drawn?.special ? (
                <div className="text-[11px] mt-1 truncate" style={{ color: PLAYERS[drawn.owner].color }}>
                  {SPECIAL_LABEL[drawn.special]}
                </div>
              ) : (
                isHumanTurn && (
                  <div className="text-[11px] text-white/65 mt-1 truncate">Tap a highlighted cell</div>
                )
              )}
              {noLegal && (
                <div className="text-[11px] mt-1" style={{ color: "#FF8A8A" }}>No legal move…</div>
              )}
            </div>
            {/* Power-up button inline (mobile) */}
            {isHumanTurn && (
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={doReroll}
                  disabled={powerupBusy || rerollsLeft <= 0}
                  className="rounded-lg px-2.5 py-1.5 text-[10px] font-display font-bold leading-tight"
                  style={{
                    background: "rgba(0,229,255,0.10)",
                    border: "1px solid rgba(0,229,255,0.45)",
                    color: rerollsLeft > 0 ? "#00E5FF" : "rgba(255,255,255,0.3)",
                    opacity: powerupBusy || rerollsLeft <= 0 ? 0.5 : 1,
                    minHeight: 40, minWidth: 44,
                  }}
                  title={`Re-roll · ${GEM_PRICES.reroll} gems · ${rerollsLeft}/${REROLL_CAP_PER_GAME}`}
                  data-testid="mobile-reroll-btn"
                >
                  <div>↻</div>
                  <div className="text-[9px] opacity-70 mt-0.5">{rerollsLeft}</div>
                </button>
              </div>
            )}
          </div>
          {powerupErr && (
            <div className="mt-2 text-[11px] text-center" style={{ color: "#FF8A8A" }}>{powerupErr}</div>
          )}
        </div>

        {/* Board column */}
        <div className="lg:col-span-9 flex flex-col items-center">
          <div className="w-full flex justify-center">
            <Board state={state} legalSet={legalSet} onCellClick={onCellClick} mode="play" />
          </div>
        </div>

        {/* Side panel (desktop only) */}
        <div className="lg:col-span-3 hidden lg:flex flex-col gap-4" data-testid="side-panel">
          <div className="glass rounded-3xl p-5">
            <div className="label-eyebrow mb-3">Current Turn</div>
            <div className="flex items-center gap-3">
              <div className="rounded-full" style={{
                width: 12, height: 12, background: PLAYERS[state.currentPlayer].color,
                boxShadow: `0 0 12px ${PLAYERS[state.currentPlayer].glow}`,
              }} />
              <div className="font-display font-bold" data-testid="current-turn-name">
                {playerNames[state.currentPlayer]}
              </div>
            </div>
          </div>

          <div className="glass rounded-3xl p-5">
            <div className="label-eyebrow mb-3">Drawn Card</div>
            <AnimatePresence mode="wait">
              {drawn ? (
                <motion.div
                  key={drawn.id}
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: -90, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  style={{ width: 120, height: 160 }}
                  data-testid="drawn-card"
                >
                  <CardView card={drawn} glow />
                </motion.div>
              ) : (
                <div style={{ width: 120, height: 160 }} className="rounded-xl bg-white/5 border border-white/10
                  flex items-center justify-center text-white/40 text-xs label-eyebrow">
                  Drawing…
                </div>
              )}
            </AnimatePresence>

            {drawn?.special && (
              <div className="mt-4">
                <div className="label-eyebrow mb-1" style={{ color: PLAYERS[drawn.owner].color }}>
                  {SPECIAL_LABEL[drawn.special]}
                </div>
                <div className="text-xs text-white/70">{SPECIAL_DESC[drawn.special]}</div>
              </div>
            )}

            {isHumanTurn && (
              <div className="mt-4 text-xs text-white/65">
                {drawn.special
                  ? "Pick a highlighted target to use your special."
                  : "Click a highlighted cell to place your card."}
              </div>
            )}
            {noLegal && (
              <div className="mt-4 text-xs" style={{ color: "#FF8A8A" }} data-testid="no-legal-msg">
                No legal move — turn passes…
              </div>
            )}

            {/* Power-up — single-player only, spend gems for an edge */}
            {isHumanTurn && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="label-eyebrow mb-2 text-white/55" style={{ fontSize: 9 }}>POWER-UPS</div>
                <button
                  onClick={doReroll}
                  disabled={powerupBusy || rerollsLeft <= 0}
                  className="w-full rounded-xl px-3 py-2 text-xs font-display font-bold transition-all"
                  style={{
                    background: "rgba(0,229,255,0.10)",
                    border: "1.5px solid rgba(0,229,255,0.45)",
                    color: rerollsLeft > 0 ? "#00E5FF" : "rgba(255,255,255,0.3)",
                    opacity: powerupBusy || rerollsLeft <= 0 ? 0.5 : 1,
                    cursor: rerollsLeft <= 0 ? "not-allowed" : "pointer",
                  }}
                  title="Discard your drawn card and draw a new one"
                  data-testid="reroll-btn"
                >
                  <div>↻ Re-roll</div>
                  <div className="text-[10px] mt-0.5 opacity-70">
                    ◈ {GEM_PRICES.reroll} · {rerollsLeft}/{REROLL_CAP_PER_GAME}
                  </div>
                </button>
                {powerupErr && (
                  <div
                    className="mt-2 text-[11px] text-center"
                    style={{ color: "#FF8A8A" }}
                    data-testid="powerup-err"
                  >{powerupErr}</div>
                )}
              </div>
            )}
          </div>

          <div className="glass rounded-3xl p-5 scroll-y" style={{ maxHeight: 220 }}>
            <div className="label-eyebrow mb-2">Game Log</div>
            <div className="space-y-1 text-xs font-mono text-white/65" data-testid="game-log">
              {state.log.length === 0 && <div>—</div>}
              {state.log.slice(-12).reverse().map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          </div>
        </div>

        {/* MOBILE-ONLY game log — collapsed by default to keep board above the fold */}
        <details className="lg:hidden glass rounded-2xl px-4 py-3" data-testid="mobile-log">
          <summary className="cursor-pointer label-eyebrow text-white/65 flex items-center justify-between">
            <span>Game Log</span>
            <span className="text-white/40 text-[10px]">tap to expand</span>
          </summary>
          <div className="mt-3 space-y-1 text-xs font-mono text-white/65 max-h-40 overflow-y-auto">
            {state.log.length === 0 && <div>—</div>}
            {state.log.slice(-12).reverse().map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </details>
      </div>

      <HowToPlay open={howto} onClose={() => setHowto(false)} />
      <ResultModal
        open={resultOpen}
        winner={state.winner}
        humanId={HUMAN_ID}
        chipsDelta={chipsDelta}
        victoryAnim={profile?.active_victory_anim}
        onPlayAgain={async () => {
          await maybeShowInterstitial();
          restart();
        }}
        onLobby={async () => {
          await maybeShowInterstitial();
          nav("/");
        }}
        onAdCredited={(chips) => {
          // Server already incremented; reflect on the local profile copy.
          setProfile((prev) => prev ? { ...prev, chips: (prev.chips || 0) + chips } : prev);
          setChipsDelta((d) => d + chips);
          // Don't double-tax: if the user just watched a rewarded ad, suppress
          // the next interstitial.
          markRewardedAdShown();
        }}
        onDoubleDownCredited={(chips) => {
          // Loss-recovery ad credit: refresh local chip balance + delta
          setProfile((prev) => prev ? { ...prev, chips: (prev.chips || 0) + chips } : prev);
          setChipsDelta((d) => d + chips);
          markRewardedAdShown();
        }}
        stake={BET}
      />
    </div>
  );
}
